import { useState } from 'react';
import { useSubmit } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { match, P } from 'ts-pattern';
import Alert from '~/components/Alert';
import Button from '~/components/button/Button';
import LinkButton from '~/components/button/LinkButton';
import Divide from '~/components/Divide';
import Page from '~/components/layout/page/Page';
import LineItem from '~/components/LineItem';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '~/components/Modal';
import { isCreditClaim } from '~/utils/claimLineItem';
import { ClaimType, ResolutionMethodLabels } from '~/utils/constants';
import { formatDate } from '~/utils/formatters';
import {
  FormKeys,
  useActionResult,
  useLoaderData,
} from './claimLineItemsSummary';

type OrderClaimLoaderData = ReturnType<typeof useLoaderData>;

type LineItem = OrderClaimLoaderData['lineItems'][number];
// ----------- utils ----------------------
const getIneligibleFootnote = (item: LineItem) => {
  switch (true) {
    case !!item.ineligibleLineItemFootnote:
      return item.ineligibleLineItemFootnote;
    case item.return.isFinalSale:
      return 'Final Sale';
    case item.currentQuantity === 0 || !item.warranty.isEligible:
      return 'Not Eligible';

    default:
      return '';
  }
};

function Footnote({ children }: { children: string }) {
  return <span className="text-critical">{children}</span>;
}

const isPastActionDate = (lastDate?: string) =>
  lastDate ? new Date(lastDate) < new Date() : false;

const getHeadlineText = (
  claimType: ClaimType,
  pendingClaims: number,
  lastActionableDate?: string,
) => {
  if (isPastActionDate(lastActionableDate))
    return 'This order is no longer eligible';

  if (pendingClaims < 1)
    return `To get started, select an eligible product for ${claimType.toLowerCase()}`;

  return 'When ready, continue to review your request';
};

// ----------- styles ----------------------
const actionStyles = 'w-full sm:w-52';

// ----------- components ----------------------
function NewClaimAction({
  item,
  claimType,
}: {
  item: LineItem;
  claimType: ClaimType;
}) {
  const submit = useSubmit();
  const select = () =>
    // submit the whole item the action has the computed available quantity on the item
    // oxlint-disable-next-line no-void -- submit is not expected to return a value; action handles changes/errors
    void submit(
      {
        [FormKeys.claimType]: claimType,
        [FormKeys.lineItem]: JSON.stringify(item),
      },
      { method: 'POST' },
    );

  return (
    <Button variant="outlined" className={actionStyles} onClick={select}>
      Select
    </Button>
  );
}

function CancelClaimAction({
  id,
  resolutionMethod,
  exchangeItem,
}: {
  id: string;
  resolutionMethod?: string;
  exchangeItem?: string;
}) {
  const [open, setOpen] = useState(false);
  const submit = useSubmit();
  const confirm = () => {
    // oxlint-disable-next-line no-void -- submit is not expected to return a value; action handles changes/errors
    void submit({ [FormKeys.claimId]: id }, { method: 'DELETE' });
    setOpen(false);
  };

  return (
    <div className={twMerge(actionStyles, 'flex flex-col')}>
      <Button
        className="border-critical text-critical focus:ring-critical sm:w-52"
        variant="outlined"
        onClick={() => setOpen(true)}
      >
        Cancel
      </Button>

      {resolutionMethod && (
        <span className="mt-2 text-center body-sm text-on-surface-variant">
          {resolutionMethod}
          {exchangeItem && (
            <>
              <span> for </span>
              <span>{exchangeItem}</span>
            </>
          )}
        </span>
      )}
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalHeader>
          <ModalTitle>Are you sure you want to cancel this request?</ModalTitle>
        </ModalHeader>
        <p className="body-md">
          Canceling will remove this request. You can still create another
          request for this item after you cancel.
        </p>
        <ModalFooter>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            No, Keep Request
          </Button>
          <Button onClick={confirm}>Yes, Cancel Request</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function LineItems({
  items,
  lastDate,
}: {
  items: OrderClaimLoaderData['lineItems'];
  lastDate?: string;
}) {
  const { claimType, claimTypeProgramName, isOutsideWindowOffer } =
    useLoaderData();

  const eligibleFor = `Eligible for ${claimTypeProgramName.toLocaleLowerCase()}`;

  // the deadline is withheld only when these items are past the window and still selectable;
  // any other missing date (lifetime warranty, no return window) keeps the program name
  const headline = match({ isOutsideWindowOffer, lastDate })
    .with({ isOutsideWindowOffer: true }, () => 'Select an item')
    .with(
      { lastDate: P.string },
      ({ lastDate: date }) => `${eligibleFor} until ${formatDate(date)}`,
    )
    .otherwise(() => eligibleFor);

  if (items.length === 0) {
    return null;
  }

  return (
    <Page.Section title={headline}>
      <Divide>
        {items.map((item) => (
          <LineItem
            key={item.id}
            name={item.name ?? ''}
            imgSrc={item.imgUrl}
            price={item.unitPrice}
            quantity={item.quantity}
            remainingReturnableQuantity={item.currentQuantity}
            options={item.optionsFromPlatform}
            footnote={
              claimType === ClaimType.return &&
              item.return.isExchangeOnly && <Footnote>Exchange Only</Footnote>
            }
          >
            <NewClaimAction item={item} claimType={claimType} />
          </LineItem>
        ))}
      </Divide>
    </Page.Section>
  );
}

function ReturningItems({
  claims,
  shopNowOffered,
}: {
  claims: OrderClaimLoaderData['pendingClaims'];
  shopNowOffered: OrderClaimLoaderData['shopNowOffered'];
}) {
  if (claims.length === 0) {
    return null;
  }

  return (
    <Page.Section title="Ready to submit">
      <Divide>
        {claims.map((cli) => {
          const { id, lineItem, requestedResolutionMethodEnum, quantity } = cli;

          return (
            <LineItem
              key={id}
              name={lineItem.name ?? ''}
              imgSrc={lineItem.imgUrl}
              price={lineItem.unitPrice}
              quantity={quantity}
              options={lineItem.optionsFromPlatform}
            >
              <CancelClaimAction
                id={id}
                resolutionMethod={
                  // oxlint-disable-next-line no-nested-ternary
                  requestedResolutionMethodEnum === 'Variant_Exchange'
                    ? ResolutionMethodLabels[requestedResolutionMethodEnum]
                    : isCreditClaim(cli)
                      ? 'Return'
                      : ''
                }
              />
            </LineItem>
          );
        })}
      </Divide>

      <LinkButton
        variant="filled"
        className="mt-2 w-full md:w-1/2"
        to={shopNowOffered ? 'shop' : 'address'}
        relative="path"
      >
        Next
      </LinkButton>
    </Page.Section>
  );
}

function IneligibleItems({
  items,
  lastDate,
}: {
  items: OrderClaimLoaderData['ineligibleItems'];
  lastDate?: string;
}) {
  const { claimTypeProgramName } = useLoaderData();

  if (items.length === 0) {
    return null;
  }

  const headlineText =
    isPastActionDate(lastDate) && lastDate
      ? `${claimTypeProgramName} period ended on ${formatDate(lastDate)}`
      : `Ineligible for ${claimTypeProgramName.toLocaleLowerCase()}`;

  return (
    <Page.Section title={headlineText}>
      <Divide>
        {items.map((item) => (
          <LineItem
            className="opacity-60"
            key={item.id}
            name={item.name ?? ''}
            imgSrc={item.imgUrl}
            price={item.unitPrice}
            quantity={item.quantity}
            remainingReturnableQuantity={item.currentQuantity}
            options={item.optionsFromPlatform}
            footnote={<Footnote>{getIneligibleFootnote(item)}</Footnote>}
          />
        ))}
      </Divide>
    </Page.Section>
  );
}

// TODO: revisit for changes by page sections
export default function ClaimLineItemsSummaryPage() {
  const {
    claimType,
    lineItems,
    pendingClaims,
    ineligibleItems,
    lastActionableDate,
    shopNowOffered,
  } = useLoaderData();

  const actionResult = useActionResult();

  const headlineText = getHeadlineText(
    claimType,
    pendingClaims.length,
    lastActionableDate,
  );

  return (
    <Page.Root title={headlineText} hideHeader>
      <LineItems items={lineItems} lastDate={lastActionableDate} />
      <IneligibleItems items={ineligibleItems} lastDate={lastActionableDate} />
      <ReturningItems claims={pendingClaims} shopNowOffered={shopNowOffered} />
      {actionResult?.ok === false && (
        <Alert variant="error" title="Problem completing your action.">
          <p>{actionResult.message}</p>
        </Alert>
      )}
    </Page.Root>
  );
}
